import { createFeatureSelector, createSelector } from '@ngrx/store'
import { isAuctionHidden } from 'libs/gb-dashboard/auctions/utils/src/lib/auction-utils'
import { PaymentProcessEnum } from 'libs/shared/models/payment-method/payment-method.models'
import { CaptureAuctionFieldEnum } from 'libs/shared/models/auction/auction.models'
import {
  initialSelectedAuctionState,
  SELECTED_AUCTION_FEATURE_KEY,
  SelectedAuctionState,
} from './selected-auction.reducer'
import { StripeAccountTypeEnum } from 'libs/gb-dashboard/shared/data-access-stripe-connect/src/lib/+state/stripe-connect.models'

const selectAuctionStateBase = createFeatureSelector<SelectedAuctionState>(SELECTED_AUCTION_FEATURE_KEY)

export const selectAuctionState = createSelector(
  selectAuctionStateBase,
  (state) => state ?? initialSelectedAuctionState,
)

export const selectLoaded = createSelector(selectAuctionState, (state) => state.loaded)

export const selectSaving = createSelector(selectAuctionState, (state) => state.saving)

export const selectAuction = createSelector(selectAuctionState, (state) => state?.auction)

export const selectUrlName = createSelector(selectAuction, (auction) => auction?.url_name || '')

export const selectCurrency = createSelector(selectAuction, (auction) => auction?.currency)

export const selectIsSaas = createSelector(selectAuction, (auction) => !!auction?.is_saas)

export const selectPreBiddingEnabled = createSelector(selectAuction, (auction) => !!auction?.pre_bidding_enabled)

export const selectBanners = createSelector(selectAuction, (auction) => auction?.banners || [])

export const selectInvoiceImage = createSelector(selectAuction, (auction) => auction?.invoice_image ?? null)

export const selectIsRaffleEnabled = createSelector(selectAuction, (auction) => auction?.raffle_item_enabled)

export const selectIsTicketingEnabled = createSelector(selectAuction, (auction) => auction?.enable_ticketing)

export const selectIsPaddleNumbersEnabled = createSelector(selectAuction, (auction) => auction?.enable_paddle_numbers)

export const selectIsBothEmailPhoneAllowed = createSelector(selectAuction, (auction) => {
  return auction?.allow_email_registration && auction?.allow_phone_registration
})

export const selectClientAccount = createSelector(selectAuction, (auction) => auction?.client_account)

export const selectCountry = createSelector(selectAuction, (auction) => auction?.country)

export const selectCommissionPercentage = createSelector(
  selectAuction,
  // use default commission percentage if undefined
  (auction) => auction?.commission_percentage ?? '4.90',
)

export const selectHasRequestInvoicePaymentMethod = createSelector(selectAuction, (auction) =>
  auction?.payment_methods?.some(
    (paymentMethod) => paymentMethod.payment_process === PaymentProcessEnum.RequestInvoice,
  ),
)

export const selectPaddleNumberRequirePaymentMethod = createSelector(
  selectAuction,
  (auction) => auction?.paddle_numbers_require_payment_method,
)

export const selectCapturePhoneNumbers = createSelector(selectAuction, (auction) => auction?.capture_phone_numbers)

export const selectCaptureTermsAndConditions = createSelector(
  selectAuction,
  (auction) => auction?.capture_terms_and_conditions_agreement,
)

export const selectIsAuctionHidden = createSelector(selectAuction, (auction) => isAuctionHidden(auction))

export const selectCreditCardPreAuthAtReg = createSelector(
  selectAuction,
  (auction) =>
    auction?.credit_card_preauthorisation === CaptureAuctionFieldEnum.Required && auction?.credit_card_preauth_at_reg,
)

export const selectTeamsCount = createSelector(selectAuction, (auction) => auction?.teams_count || 0)

export const selectCaptureTeams = createSelector(selectAuction, (auction) => auction?.capture_teams)

export const selectEventTicketCaptureEmail = createSelector(
  selectAuction,
  (auction) => auction?.event_ticket_capture_email,
)

export const selectEventTicketCapturePhoneNumber = createSelector(
  selectAuction,
  (auction) => auction?.event_ticket_capture_phone_number,
)

export const selectTicketsLabel = createSelector(selectAuction, (auction) => auction?.tickets_label)

export const selectPaddleStartNumber = createSelector(selectAuction, (auction) => auction?.paddle_start_number)

export const selectMetadata = createSelector(selectAuction, (auction) => auction?.metadata)

export const stripeUserId = createSelector(selectAuction, (auction) => auction?.stripe_user_id || '')

export const isStripeExpress = createSelector(
  selectAuction,
  (auction) => auction?.stripe_connect?.account_type === StripeAccountTypeEnum.Express,
)

export const tippingSliderAmountOptions = createSelector(
  selectAuction,
  (auction) => auction?.tipping_slider_amount_options,
)
